import { VideoElementSaveModel } from "client/Api/Hub/common.composer";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ensureNotNil,
	generateTrackingId,
	setTrackingId
} from "client/Misc/utils";
import {
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useRef
} from "react";
import { useBuilderEditor } from "../../useBuilderEditor";
import { VideoViewer } from "../../Viewer/Elements/Video/Video";
import styles from "./Video.module.less";
import { ElementLayout } from "../ElementLayout";
import { ElementGenericActionHandlers, ElementGenericHandle } from "../Element";
import { ValidationError } from "client/Components";
import { Guid } from "client_legacy/ndaw/guid";
import { getClientId, hasClientId } from "client/Misc/clientId";

export type VideoProps = {
	attachable: IValueInputAttachable<VideoElementSaveModel>;
	genericHandlers: ElementGenericActionHandlers;
	ref: Ref<ElementGenericHandle>;
};

export const Video = (props: VideoProps): ReactElement => {
	const {
		attachable,
		genericHandlers,
		ref
	} = props;

	const {
		onAddNewElementToColumn,
		onOpenEmbedInputDialog,
		onOpenVideoInputDialog,
		onSelectImage,
		onMoveUp,
		onMoveDown,
		onDuplicate,
		onDelete
	} = genericHandlers;

	const {
		activate
	} = useBuilderEditor();

	const rootRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);

	const trackingId: string = attachable.reducer
		.path;

	const { url } = attachable.value;

	const editHandler = useCallback(
		async (): Promise<void> => {
			const result: TryResult<string> = await onOpenVideoInputDialog({
				videoUrl: attachable.value.url
			});

			if (result.wasSuccessful) {
				attachable.for("url")
					.reducer.setValue(result.result);
				attachable.for("url")
					.validation.triggerInputFinished();
			}
		},
		[attachable, onOpenVideoInputDialog]
	);

	useImperativeHandle(
		ref,
		(): ElementGenericHandle => ({
			findAndFocus: (clientId: Guid): void => {
				if (hasClientId(attachable.value) && getClientId(attachable.value) === clientId) {
					ensureNotNil(rootRef.current)
						.focus();
				}
			},
			scrollIntoView: (): void => {
				ensureNotNil(rootRef.current)
					.scrollIntoView();
			}
		}),
		[attachable.value]
	);

	return (
		<ElementLayout
			hasError={!attachable.validation.isValid()}
			trackingId={attachable.reducer.path}
			ref={rootRef}
		>
			<ElementLayout.ActionsMenu trackingId="element-actions">
				<ElementLayout.AddElementMenuItem
					onAddNewElementToColumn={onAddNewElementToColumn}
					onOpenEmbedInputDialog={onOpenEmbedInputDialog}
					onOpenVideoInputDialog={onOpenVideoInputDialog}
					onSelectImage={onSelectImage}
					trackingId={generateTrackingId(attachable.reducer.path, "__add-element-menu")}
				/>
				{onMoveUp && (
					<ElementLayout.MoveUpMenuItem
						onClick={onMoveUp}
					/>
				)}
				{onMoveDown && (
					<ElementLayout.MoveDownMenuItem
						onClick={onMoveDown}
					/>
				)}
				<ElementLayout.EditMenuItem
					onClick={editHandler}
				/>
				<ElementLayout.DuplicateMenuItem
					onClick={onDuplicate}
				/>
				<ElementLayout.DeleteMenuItem
					onClick={onDelete}
				/>
			</ElementLayout.ActionsMenu>
			<div
				{...setTrackingId(trackingId, "__video")}
				onClick={() => activate()}
				className={styles.video}
			>
				<VideoViewer url={url} />
			</div>
			<ValidationError
				{...attachable.for("url").attachToValidationError()}
			/>
		</ElementLayout>
	);
};
Video.displayName = "Video";
